/**
 * T485 — town matching for free-text customer input.
 *
 * The customer types a town in their own words; this resolves it to a curated
 * gazetteer name so the priced lane can be reached, or reports honestly that it
 * could not. Pure: NO pg, NO fetch, NO DOM, and ZERO imports — it is bundled
 * into the browser client (esbuild --target=safari14.1), so anything that pulls
 * a node builtin breaks `npm run build:client` while every other check stays
 * green.
 *
 * THE LOAD-BEARING RULE — tiers split by the NATURE of the match, not by score:
 *
 *   exact / normalized / alias   deterministic EQUALITY  -> caller may auto-pick
 *   fuzzy                        INFERENCE               -> suggest only, never auto-pick
 *
 * Normalization is an extension of the case-fold the resolver already does
 * (actions.ts): it additionally folds diacritics and collapses punctuation and
 * whitespace. It cannot conflate two gazetteer towns — no two of the 42 curated
 * names (plus declared aliases) share a normalized form, which is what makes the
 * deterministic tiers safe to auto-pick. Fuzzy is inference and must never
 * select a price: it exists to offer the operator a one-tap suggestion, and its
 * caller is required to keep the customer's verbatim text on screen beside it.
 * "A loud dead end still beats a quiet wrong pick" (locations-repo.ts).
 */

/** Jaro-Winkler acceptance floor. Shared semantics with the customer-name
 *  dedupe (customers-repo.ts FUZZY_THRESHOLD). Calibrated against the real
 *  gazetteer: the worst DISTINCT-town collision is 0.8700 (Glenealy vs
 *  Glenmalure) and the worst genuinely-unknown town is 0.8493 (Ballybofey vs
 *  Ballybeg House), while the weakest real typo is 0.9333 (Bry -> Bray). 0.92
 *  sits in that gap with margin on both sides. */
export const FUZZY_THRESHOLD = 0.92;

/** How far clear of the runner-up a fuzzy winner must be. Against today's 42
 *  towns this never decides an outcome — the threshold does — but the gazetteer
 *  grows, and a near-tie must manualize rather than guess. Smallest margin among
 *  accepted typos measured: 0.0822, i.e. 16x clear of the boundary.
 *
 *  Do NOT pin the exact boundary in a spec: at a NOMINAL margin of 0.05 the
 *  verdict is float-dependent, not deterministic (0.9 - 0.85 =
 *  0.050000000000000044 picks; 0.95 - 0.90 = 0.04999999999999993 is ambiguous).
 *  Left alone deliberately — the ambiguous half is the safe side, and real
 *  margins sit nowhere near the noise floor. */
export const AMBIGUITY_MARGIN = 0.05;

/**
 * Longest query worth matching. Jaro is O(n*m) and runs once per candidate, so
 * an unbounded string costs 42 * n * (town length) per call — and the kiosk's
 * search box, unlike the public form's 60-char town field, has no maxlength and
 * matches on every keystroke. Set to the same 200 the server uses on this input
 * (app.ts handleLocations) so both ends draw the line in the same place — though
 * the server TRUNCATES to 200 while this REFUSES past it, which is the safer of
 * the two here: a silently truncated town could still match something, whereas a
 * refusal falls through to manual. No real place name is remotely near this.
 */
export const MAX_QUERY_LEN = 200;

/**
 * Case-fold, strip diacritics, collapse everything non-alphanumeric to single
 * spaces. `'Dún Laoghaire'` and `'  DUN  laoghaire '` both become
 * `'dun laoghaire'` — which is the whole reason a fada-less spelling can reach
 * its priced row (the DB search is ILIKE over the raw label and never matches
 * across the accent).
 */
export function normalizeTown(s: string): string {
  return s
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/**
 * Jaro similarity. Mirrors natural@8's `distance()` exactly, including its
 * UNCLAMPED match window — see jaroWinkler below for why byte-parity matters.
 */
function jaro(a: string, b: string): number {
  if (a === b) return 1;
  if (a.length === 0 || b.length === 0) return 0;

  const matchWindow = Math.floor(Math.max(a.length, b.length) / 2) - 1;
  const aMatched: boolean[] = new Array(a.length).fill(false);
  const bMatched: boolean[] = new Array(b.length).fill(false);
  let matches = 0;

  for (let i = 0; i < a.length; i++) {
    const start = Math.max(0, i - matchWindow);
    const end = Math.min(i + matchWindow + 1, b.length);
    for (let k = start; k < end; k++) {
      if (bMatched[k]) continue;
      if (a[i] !== b[k]) continue;
      aMatched[i] = true;
      bMatched[k] = true;
      matches++;
      break;
    }
  }
  if (matches === 0) return 0;

  let transpositions = 0;
  let k = 0;
  for (let i = 0; i < a.length; i++) {
    if (!aMatched[i]) continue;
    while (!bMatched[k]) k++;
    if (a[i] !== b[k]) transpositions++;
    k++;
  }
  transpositions /= 2;

  return (
    matches / a.length +
    matches / b.length +
    (matches - transpositions) / matches
  ) / 3;
}

/**
 * Jaro-Winkler, p = 0.1, prefix capped at 4.
 *
 * DELIBERATELY UNGATED: the textbook form only applies the prefix boost when
 * jaro > 0.7, but natural@8 applies it unconditionally. Every threshold in this
 * file was calibrated against natural's numbers, and the ungated form reproduces
 * them EXACTLY (0 numeric differences over 1995 measured pairs) whereas the
 * gated form differs in 118. Do not add a `jaro > 0.7` gate "to match the
 * textbook" — it would silently drift the implementation off the calibration.
 * (Both forms happen to be decision-equivalent at 0.92, because divergence
 * requires jaro <= 0.7, which bounds the score at 0.7 + 4*0.1*0.3 = 0.82. The
 * ungated form simply needs no such argument.)
 */
export function jaroWinkler(a: string, b: string): number {
  if (a === b) return 1;
  const j = jaro(a, b);
  let prefix = 0;
  while (prefix < 4 && a[prefix] !== undefined && a[prefix] === b[prefix]) prefix++;
  return j + prefix * 0.1 * (1 - j);
}

/** A candidate town: its canonical name plus any DECLARED aliases (curated
 *  data, never inference). Shaped to accept GazetteerEntry directly. */
export type TownCandidate = {
  name: string;
  aliases?: readonly string[];
};

export type TownMatch =
  /** Deterministic equality — the caller MAY auto-pick these. */
  | { kind: 'exact'; name: string }
  | { kind: 'normalized'; name: string }
  | { kind: 'alias'; name: string }
  /** Inference — SUGGEST ONLY. The caller must not auto-pick. */
  | { kind: 'fuzzy'; name: string; score: number }
  /** Two or more candidates too close to separate — manual, never a guess. */
  | { kind: 'ambiguous' }
  | { kind: 'none' };

/** True for the tiers that are safe to auto-pick into the priced lane. */
export function isDeterministic(m: TownMatch): boolean {
  return m.kind === 'exact' || m.kind === 'normalized' || m.kind === 'alias';
}

/**
 * Resolve free text to a candidate town.
 *
 * Tier order is deliberate: curated data outranks inference at every step, so
 * an alias hit ('Newtownmountkennedy' -> 'Newtown', a declared same-place short
 * form) wins before fuzzy ever runs — it scores only 0.8737 and would otherwise
 * be refused.
 *
 * Alias matching is EXACT after normalization, NOT the containment predicate
 * `aliasCanonicalNames` uses. Containment is safe there (a zero-result fallback
 * that can only ADD rows) but not here: this tier auto-picks, and under
 * containment the query 'town' would resolve to Newtown via
 * 'Newtownmountkennedy'.
 */
export function matchTown(
  query: string,
  candidates: readonly TownCandidate[],
): TownMatch {
  const raw = query.trim();
  if (raw === '' || raw.length > MAX_QUERY_LEN) return { kind: 'none' };
  const q = normalizeTown(raw);
  if (q === '') return { kind: 'none' };

  // 1. Exact label (case-insensitive) — what the resolver already accepts.
  for (const c of candidates) {
    if (c.name.toLowerCase() === raw.toLowerCase()) return { kind: 'exact', name: c.name };
  }

  // 2. Normalized label equality — folds diacritics, spacing and punctuation.
  const normHits = candidates.filter((c) => normalizeTown(c.name) === q);
  // A second hit would mean two curated towns share a normalized form. That is
  // false today and asserted by a spec; refuse rather than pick if it changes.
  if (normHits.length === 1 && normHits[0]) return { kind: 'normalized', name: normHits[0].name };
  if (normHits.length > 1) return { kind: 'ambiguous' };

  // 3. Declared aliases, exact after normalization.
  const aliasHits = candidates.filter((c) =>
    (c.aliases ?? []).some((a) => normalizeTown(a) === q),
  );
  if (aliasHits.length === 1 && aliasHits[0]) return { kind: 'alias', name: aliasHits[0].name };
  if (aliasHits.length > 1) return { kind: 'ambiguous' };

  // 4. Fuzzy — INFERENCE. Scored per TOWN, not per string: a town's aliases are
  //    alternative spellings of the SAME place, so they compete for its own best
  //    score and must never register as a rival (which would make the margin
  //    rule veto a match against itself).
  let best: { name: string; score: number } | null = null;
  let runnerUp = -1;
  for (const c of candidates) {
    let score = jaroWinkler(q, normalizeTown(c.name));
    for (const a of c.aliases ?? []) {
      score = Math.max(score, jaroWinkler(q, normalizeTown(a)));
    }
    if (best === null || score > best.score) {
      if (best !== null) runnerUp = best.score;
      best = { name: c.name, score };
    } else if (score > runnerUp) {
      runnerUp = score;
    }
  }

  if (best === null || best.score < FUZZY_THRESHOLD) return { kind: 'none' };
  if (runnerUp >= 0 && best.score - runnerUp < AMBIGUITY_MARGIN) return { kind: 'ambiguous' };
  return { kind: 'fuzzy', name: best.name, score: best.score };
}
